

// components/common/CategoryTreeFilter.tsx
import { useState } from 'react';
import { ChevronRight, FolderOpen, Folder } from 'lucide-react';
import { useParentCategories, useSubcategories } from '@/hooks/useCategories';
import { useNavigate } from 'react-router-dom';

interface CategoryTreeFilterProps {
  selectedCategoryId?: string | null;
  onSelect: (id: string | null, category?: any) => void;
}

/**
 * CategoryTreeFilter — Premium, minimal category tree with elegant animations
 * Uses the site palette: terracotta primary for accents, ivory backgrounds, subtle borders.
 */
const PRIMARY = '#A8523A';
const PRIMARY_TINT = 'rgba(168,82,58,0.125)';
const TEXT = '#2D2D2D';
const MUTED = '#F5F2ED';

export const CategoryTreeFilter = ({ selectedCategoryId, onSelect }: CategoryTreeFilterProps) => {
  const { data: parentCategories, isLoading } = useParentCategories();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-2 p-1">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            style={{
              background:
                'linear-gradient(90deg, rgba(0,0,0,0.02) 0%, rgba(255,255,255,0.9) 50%, rgba(0,0,0,0.02) 100%)',
            }}
            className="h-9 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!parentCategories?.length) {
    return (
      <div className="text-center py-8 px-4">
        <Folder className="w-10 h-10 text-gray-300 mx-auto mb-2" />
        <p style={{ color: 'rgba(45,45,45,0.55)' }} className="text-sm">No categories available</p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {parentCategories.map((cat) => (
        <CategoryNode
          key={cat.id}
          category={cat}
          level={0}
          selectedCategoryId={selectedCategoryId}
          onSelect={onSelect}
          navigate={navigate}
        />
      ))}
    </div>
  );
};

interface CategoryNodeProps {
  category: any;
  level: number;
  selectedCategoryId?: string | null;
  onSelect: (id: string | null, category?: any) => void;
  navigate: any;
}

/**
 * CategoryNode — Individual category with smooth expand/collapse
 */
const CategoryNode = ({ category, level, selectedCategoryId, onSelect, navigate }: CategoryNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const { data: subcategories, isLoading } = useSubcategories(category.id);

  const hasChildren = category.hasSubcategories || (subcategories && subcategories.length > 0);
  const isSelected = selectedCategoryId === category.id;

  const handleSelect = () => {
    // Call the onSelect callback with category object first
    onSelect(category.id, category);
    
    // Navigate to the category URL using seoUrl
    if (category.seoUrl) {
      navigate(`/products/${category.seoUrl}`);
    } else {
      console.warn('Category missing seoUrl:', category);
    }
  };

  return (
    <div >
      <div
        className={`
          group relative flex items-center justify-between cursor-pointer
          rounded-lg transition-all duration-200 ease-out
          ${level === 0 ? 'py-2.5 px-3' : 'py-2 px-3'}
        `}
        style={{
          paddingLeft: `${level * 20 + 12}px`,
          marginLeft: level > 0 ? '8px' : '0',
          background: isSelected ? PRIMARY_TINT : 'transparent',
          border: isSelected ? `1px solid ${PRIMARY_TINT}` : '1px solid transparent',
        }}
        onClick={handleSelect}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Left side: expand button + icon + name */}
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          {/* Expand/collapse button */}
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((prev) => !prev);
              }}
              className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-md transition-all duration-200`}
              aria-label={expanded ? 'Collapse' : 'Expand'}
              style={{
                color: isSelected || isHovered ? PRIMARY : 'rgba(45,45,45,0.45)',
              }}
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>
          ) : (
            <div className="w-5" />
          )}

          {/* Folder icon */}
          <div style={{ color: isSelected ? PRIMARY : isHovered ? 'rgba(45,45,45,0.8)' : 'rgba(45,45,45,0.45)' }}>
            {expanded ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />}
          </div>

          {/* Category name */}
          <span
            className={`text-sm truncate transition-all duration-200`}
            style={{
              color: isSelected ? TEXT : isHovered ? 'rgba(45,45,45,0.9)' : 'rgba(45,45,45,0.7)',
              fontWeight: isSelected ? 600 : 500,
            }}
          >
            {category.name}
          </span>
        </div>

        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div style={{ background: PRIMARY }} className="w-1.5 h-1.5 rounded-full animate-pulse" />
          </div>
        )}
      </div>

      {/* Children with smooth animation */}
      {expanded && (
        <div
          className="overflow-hidden animate-in slide-in-from-top-1 duration-200"
          style={{
            borderLeft: level === 0 ? `2px solid ${MUTED}` : 'none',
            marginLeft: level === 0 ? '20px' : '0',
          }}
        >
          {isLoading ? (
            <div className="space-y-1.5 py-2 pl-2">
              {[...Array(2)].map((_, i) => (
                <div
                  key={i}
                  className="h-8 rounded-lg mx-2"
                  style={{
                    background:
                      'linear-gradient(90deg, rgba(0,0,0,0.02) 0%, rgba(255,255,255,0.9) 50%, rgba(0,0,0,0.02) 100%)',
                  }}
                />
              ))}
            </div>
          ) : subcategories?.length ? (
            <div className="py-1">
              {subcategories.map((sub) => (
                <CategoryNode
                  key={sub.id}
                  category={sub}
                  level={level + 1}
                  selectedCategoryId={selectedCategoryId}
                  onSelect={onSelect}
                  navigate={navigate}
                />
              ))}
            </div>
          ) : (
            <div className="py-2 pl-12">
              <p style={{ color: 'rgba(45,45,45,0.45)' }} className="text-xs italic">No subcategories</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryTreeFilter;
